// Customer-support settings: numbering, statuses, auto-assignment, weekend
// rule. Stored under userFilters/ (staff-writable node — rules deploys are
// gated). A module-level cache lets non-React code (ticketService) read the
// current settings synchronously.
import { useEffect, useState } from 'react';
import { ref, onValue, update, get } from 'firebase/database';
import { db } from '../../../integrations/firebase';
import { STATUSES, STATUS_LABELS } from '../constants';

export const SUPPORT_SETTINGS_PATH = 'userFilters/supportSettings';

// Statuses that drive workflow logic — can be hidden from the picker but never
// deleted (refund/replacement/resolution flows depend on them).
export const CORE_STATUSES = new Set(['open', 'pending', 'resolved', 'closed']);

export const DEFAULT_SETTINGS = {
    // Ticket number format: {prefix}{separator}{zero-padded sequence}
    numbering: { prefix: 'TKT', separator: '-', digits: 6 },
    // Weekend rule: complaints registered Saturday after cutoff (or Sunday) are
    // treated as registered Monday 09:00 for SLA + performance purposes.
    weekendRule: { enabled: true, cutoffHour: 16, resumeHour: 9 },
    // First status change by an agent auto-assigns the (unassigned) ticket to them.
    autoAssignOnStatusChange: true,
    // Auto-assignment rules at creation: { issueType | department : { uid, name } }
    autoAssign: {},
    // Custom statuses: [{ key, label }]; hidden built-in statuses: [key]
    customStatuses: [],
    hiddenStatuses: [],
};

let cache = { ...DEFAULT_SETTINGS };
let started = false;
let loaded = false;
const listeners = new Set();
const normalize = (raw) => {
    const s = { ...DEFAULT_SETTINGS, ...(raw || {}) };
    s.weekendRule = { ...DEFAULT_SETTINGS.weekendRule, ...(raw?.weekendRule || {}) };
    s.numbering = { ...DEFAULT_SETTINGS.numbering, ...(raw?.numbering || {}) };
    s.customStatuses = Array.isArray(s.customStatuses) ? s.customStatuses : Object.values(s.customStatuses || {});
    s.hiddenStatuses = Array.isArray(s.hiddenStatuses) ? s.hiddenStatuses : Object.values(s.hiddenStatuses || {});
    s.autoAssign = s.autoAssign || {};
    return s;
};
export const startSupportSettings = () => {
    if (started) return;
    started = true;
    onValue(ref(db, SUPPORT_SETTINGS_PATH), (snap) => {
        cache = normalize(snap.exists() ? snap.val() : null);
        loaded = true;
        listeners.forEach(fn => fn(cache));
    }, () => { /* keep defaults */ });
};
export const getSupportSettings = () => cache;
// The cache holds DEFAULTS until the listener's first answer. A ticket raised from a
// screen that never opened the support module (e.g. from a call) was numbered with the
// default TKT-000231 instead of the configured DOC0231 (31 Oct 2026). Anything that
// writes with the settings must await this first.
export const ensureSupportSettings = async () => {
    if (loaded) return cache;
    startSupportSettings();
    try {
        const snap = await get(ref(db, SUPPORT_SETTINGS_PATH));
        if (!loaded) { cache = normalize(snap.exists() ? snap.val() : null); loaded = true; }
    } catch { /* keep defaults */ }
    return cache;
};
export const saveSupportSettings = (partial) => update(ref(db, SUPPORT_SETTINGS_PATH), partial);

// React hook: live settings.
export const useSupportSettings = () => {
    const [s, setS] = useState(cache);
    useEffect(() => {
        startSupportSettings();
        listeners.add(setS);
        setS(cache);
        return () => listeners.delete(setS);
    }, []);
    return s;
};

// Effective status list + labels (built-ins minus hidden, plus custom).
export const effectiveStatuses = (s = cache) => {
    const hidden = new Set(s.hiddenStatuses || []);
    const base = STATUSES.filter(k => !hidden.has(k));
    const custom = (s.customStatuses || []).map(c => c.key).filter(Boolean);
    return [...base, ...custom.filter(k => !base.includes(k))];
};
export const effectiveStatusLabels = (s = cache) => {
    const labels = { ...STATUS_LABELS };
    (s.customStatuses || []).forEach(c => { if (c.key) labels[c.key] = c.label || c.key; });
    return labels;
};

// Weekend rule: effective registration time for SLA/performance.
export const effectiveStart = (createdAt, s = cache) => {
    const r = s.weekendRule || DEFAULT_SETTINGS.weekendRule;
    if (!r.enabled || !createdAt) return createdAt;
    const d = new Date(createdAt);
    const day = d.getDay(); // 0 Sun … 6 Sat
    const afterCutoff = day === 6 && d.getHours() >= (r.cutoffHour ?? 16);
    if (!afterCutoff && day !== 0) return createdAt;
    const monday = new Date(d);
    monday.setDate(d.getDate() + (day === 6 ? 2 : 1));
    monday.setHours(r.resumeHour ?? 9, 0, 0, 0);
    return monday.getTime();
};

// Format a sequence number per the numbering settings (e.g. DO-000123, CMP0042).
export const formatTicketNumber = (n, s = cache) => {
    const num = s.numbering || DEFAULT_SETTINGS.numbering;
    const prefix = String(num.prefix || 'TKT').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6) || 'TKT';
    const sep = ['-', '', '/', '_'].includes(num.separator) ? num.separator : '-';
    const digits = Math.min(8, Math.max(3, Number(num.digits) || 6));
    return `${prefix}${sep}${String(n).padStart(digits, '0')}`;
};

// Auto-assignment rule lookup: issue type first, then department.
export const autoAssigneeFor = (issueType, department, s = cache) => {
    const rules = s.autoAssign || {};
    return rules[issueType] || rules[`dept:${department}`] || null;
};
